import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Cms, ICarDetails, TempAny } from '@impact/data';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CarDetailsService } from '../../car-details-service/car-details.service';
import { COMPONENT_LOADER_SIZE } from '../../component-loader/component-loader.component';
import { ComponentLoaderModule } from '../../component-loader/component-loader.module';
import { MetaService } from '../../meta/meta.service';
import { UsedCarDetailsModule } from '../../used-car-details/used-car-details.module';
import { ClientRoutingService } from '../../utils/helpers/client-routing.service';
import { mapProductToEcommerceVehicle } from '../../utils/tracking/tracking-product.mapper';
import { TrackingService } from '../../utils/tracking/tracking.service';
import { SettingsService, SiteIds } from '../../core/settings.service';
import { SpotsUiModule } from '../../spots/spots-ui.module';

@Component({
    selector: 'app-used-car-details-page',
    template: `
        <main>
            <ng-container [ngSwitch]="usedCarsDataReady">
                <ng-container *ngSwitchCase="true">
                    <ng-container *ngIf="data && usedCar" [ngSwitch]="site === siteIds.MOTORPOINT">
                        <ng-container *ngSwitchCase="true">
                            <app-used-car-details-motorpoint [carDetails]="usedCar" [pageData]="data"></app-used-car-details-motorpoint>
                        </ng-container>
                        <ng-container *ngSwitchCase="false">
                            <app-used-car-details [carDetails]="usedCar" [pageData]="data"></app-used-car-details>
                        </ng-container>
                    </ng-container>

                    <section class="page-grid-content">
                        <app-spot-grid [grid]="data?.pageBottomGrid"></app-spot-grid>
                    </section>
                </ng-container>
                <app-component-loader *ngSwitchCase="false"
                    [isPageSize]="true">
                </app-component-loader>
            </ng-container>
        </main>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
class UsedCarDetailsPageComponent implements OnInit, OnDestroy {
    @Input() data: Cms.UsedCarDetailsPage;

    pageData: TempAny;
    usedCar: ICarDetails;
    usedCarsDataReady = false;

    componentLoaderSize = COMPONENT_LOADER_SIZE;
    site: string;
    siteIds = SiteIds;

    private unsubscribe = new Subject<void>();

    constructor(
        private readonly carDetailsService: CarDetailsService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly cd: ChangeDetectorRef,
        private readonly metaService: MetaService,
        private readonly translateService: TranslateService,
        private readonly clientRoutingService: ClientRoutingService,
        private readonly trackingService: TrackingService,
        private readonly settingsService: SettingsService
    ) {
        this.settingsService.get().pipe(takeUntil(this.unsubscribe)).subscribe((settings) => {
            this.site = settings.siteNameId;
        });
    }

    ngOnInit() {
        // Get vehicle ID from URL
        const currentUrl = this.router.url.split('?')[0].split('#')[0];
        const parts = currentUrl.split('/');
        const vehicleId = parts[parts.length - 1];

        this.carDetailsService
            .fetchCarDetailsForId(vehicleId)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(
                (carDetails) => {
                    if (!carDetails.car) {
                        console.warn('should return 404');
                        this.clientRoutingService.redirectToNotFoundPage();
                        return;
                    }

                    const currentUrl = `/${this.route.snapshot.url.join('/')}`;
                    const validUrls = this.carDetailsService.generateUrl(carDetails.car);

                    if (!validUrls.some((url) => url === currentUrl)) {
                        // TODO: Return 301
                        console.warn('should return 301', currentUrl);
                        return;
                    }

                    this.usedCar = carDetails.car;
                    this.carDetailsService.setCurrentMakeAndModel(this.usedCar?.make?.value, this.usedCar?.model?.value, this.usedCar?.id);
                    this._setMetaData();
                    this.carDetailsService.setVisitedCarId(this.usedCar.id);
                    this.usedCarsDataReady = true;
                    this.cd.markForCheck();
                },
                (error) => {
                    console.warn('Failed to get car details: ', error);
                    this.clientRoutingService.redirectToNotFoundPage();
                }
            );
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    private _setMetaData(): void {
        if (!this.data || !this.usedCar) {
            return;
        }

        const { make, model, variant, dealer, color, motor, price, pictures, year } = this.usedCar;
        const { metaDescription: metaDescription, metaKeywords: metaKeywords, metaTitle: metaTitle } = this.data;

        const displayName = `${make?.value || ''} ${model?.value || ''} ${variant?.value || ''}`.trim();

        const interpolatableParams = {
            displayName,
            color: color?.value,
            make: make?.value,
            model: model?.value,
            motor: motor?.value,
            price: price?.value,
            variant: variant?.value,
            year: year?.value,
            dealerLocation: dealer?.data?.dealerName?.value,
        };

        let title = '';

        if (metaTitle) {
            title = this.translateService.parser.interpolate(metaTitle, interpolatableParams) ?? '';
            this.metaService.setTitle(title);
        }

        if (metaDescription) {
            const description = this.translateService.parser.interpolate(metaDescription, interpolatableParams);
            this.metaService.setDescription(description);
        }

        if (metaKeywords) {
            const keywords = this.translateService.parser.interpolate(metaKeywords, interpolatableParams);
            this.metaService.setKeywords(keywords);
        }

        if (pictures?.length) {
            const mainImage = pictures[0];
            if (mainImage) {
                this.metaService.setOpenGraph({
                    title,
                    type: 'website',
                    image: {
                        url: mainImage,
                        width: '1600',
                        height: '1067',
                        altText: title,
                    },
                    author: dealer?.data?.dealerName?.value || '',
                });
            }
        }

        this.trackingService.trackVirtualPageViewPdp(this.usedCar, title);

        try {
            const ecommerceProduct = mapProductToEcommerceVehicle(this.usedCar);
            this.trackingService.trackEcommerce(
                {
                    detail: {
                        products: [ecommerceProduct],
                    },
                },
                true
            );
        } catch (e: any) {
            console.warn('Failed to track impression: ', e.message);
        }
    }
}

@NgModule({
    declarations: [UsedCarDetailsPageComponent],
    imports: [
        CommonModule,
        SpotsUiModule,
        UsedCarDetailsModule,
        ComponentLoaderModule
    ],
})
class UsedCarDetailsPageModule {}

export const LazyComponent = UsedCarDetailsPageComponent;
